import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from './AppContext';
import { getFirestore, runTransaction, addDoc, deleteDoc, setDoc, serverTimestamp, doc, getDoc, collection, query, where, getDocs, orderBy, startAfter, limit } from 'firebase/firestore';
import { firestore } from './firebase';

const CACHE_DURATION = 12;

const GenresScreen = () => {
  const navigate = useNavigate();
  const { currentUser, assetsSummary, userWallet, library, allSingles, allAlbums } = useContext(AppContext);

  const genresList = [
    { label: "Alternative", value: "alternative" },
    { label: "Blues", value: "blues" },
    { label: "Classical", value: "classical" },
    { label: "Country", value: "country" },
    { label: "Electronic/Dance", value: "electronic_dance" },
    { label: "Folk", value: "folk" },
    { label: "Rap", value: "hip_hop_rap" },
    { label: "Indie", value: "indie" },
    { label: "Jazz", value: "jazz" },
    { label: "Latin", value: "latin" },
    { label: "Metal", value: "metal" },
    { label: "Pop", value: "pop" },
    { label: "R&B/Soul", value: "rnb_soul" },
    { label: "Reggae", value: "reggae" },
    { label: "Rock", value: "rock" },
    { label: "World/International", value: "world_international" }
  ];

  const getCachedUserDoc = async (userId) => {
    let userDataCached = [];
    let userDataCachedNew = [];
    const now = new Date().getTime();
  
    if (!userDataCached || now - userDataCached.timestamp >= CACHE_DURATION) {
        
        const userDocRef = doc(firestore, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);
  
        if (userDocSnap.exists()) {
            
            const freshUserData = userDocSnap.data();
  
            
            // Update userDataCached structure with fresh data and timestamp
            userDataCachedNew = {
                ...freshUserData, // overwrite with fresh data from Firestore
                profileImageUrl: freshUserData?.profileImageUrl,
                timestamp: now
            };
  
  
        } else {
            console.error(`[getCachedUserDoc] No document found in Firestore for userId: ${userId}`);
        }
    } else {
        
        userDataCachedNew = userDataCached;
    }
  
    return userDataCachedNew;
  };

  const genreLabelMap = new Map(genresList.map(item => [item.value, item.label]));
  genreLabelMap.set("hip_hop", "Rap");
  
  const genres = useMemo(() => {
    const genreMap = new Map();
    const myArtist = currentUser?.artistName;
  
    // Normalize & map a raw genre string to your value + label
    const normalize = raw => {
      const base = raw.split(' -')[0].trim().toLowerCase();
      return base === 'hip_hop' ? 'hip_hop_rap' : base;
    };
  
    // Helper: add one asset under all its genres, marking NFT or not
    const addAsset = (asset, isNFT) => {
      if (!Array.isArray(asset.genres)) return;
      const id = asset.id || asset.contentId;
      for (const raw of asset.genres) {
        const key = normalize(raw);
        const label = genreLabelMap.get(key);
        if (!label) continue;                // skip genres you don't recognize
  
        if (!genreMap.has(key)) {
          genreMap.set(key, { label, items: [], seen: new Set() });
        }
        const bucket = genreMap.get(key);
        if (!bucket.seen.has(id)) {
          bucket.seen.add(id);
          bucket.items.push({ ...asset, isNFT, contentId: id });
        }
      }
    };
  
    const ownedIds = new Set(userWallet?.ownedDats?.map(i => i.id) || []);
  
    // 1️⃣ Owned DATS (NFT)
    for (const a of assetsSummary || []) {
      if (ownedIds.has(a.id)) {
        addAsset(a, true);
      }
    }

    // 2️⃣ LIBRARY ITEMS (non-NFT) — ANY library entry that has genres
for (const libItem of library || []) {
  if (!Array.isArray(libItem.genres)) continue;
  // you can either add libItem directly or, if you want to prefer
  // the richer data in assetsSummary, try matching it first:
  const cid = libItem.contentId || libItem.id;
  const match = (assetsSummary || []).find(
    a => a.id === cid || a.contentId === cid
  );
  const assetToAdd = match || libItem;
  addAsset(assetToAdd, false);
}

  
    // 2️⃣ Library extras (only those in assetsSummary, NFT)
    {
      const added = new Set(
        [...genreMap.values()].flatMap(b => [...b.seen])
      );
      for (const libItem of library || []) {
        const match = (assetsSummary || []).find(a => a.id === libItem.contentId);
        if (match && !added.has(match.id)) {
          addAsset(match, true);
          added.add(match.id);
        }
      }
    }
  
    // 3️⃣ Any of *your* releases in assetsSummary (even if not owned) — treat NFT
    for (const a of assetsSummary || []) {
      if (a.ArtistName === myArtist) {
        addAsset(a, true);
      }
    }
  
    // 4️⃣ Any of *your* singles — non-NFT
    for (const s of allSingles || []) {
      if (s.ArtistName === myArtist) {
        addAsset(s, false);
      }
    }
  
    // 5️⃣ Any of *your* albums — non-NFT
    for (const alb of allAlbums || []) {
      if (alb.ArtistName === myArtist) {
        addAsset(alb, false);
      }
    }
  
    // Build final array, stripping out our internal `seen` sets:
    return Array.from(genreMap, ([value, { label, items }]) => ({
      name: label,
      value,
      label,
      items
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [assetsSummary, userWallet, library, allSingles, allAlbums, currentUser]);

  const handleSelectGenre = (genreValue) => {
    navigate('/library/genres/genremedialist', { state: { genre: genreValue } });
  };
  

  return (
    <div style={styles.fullScreen}>
      <img src={require('./assets/BlankBG.png')} style={styles.fullScreenImage} alt="Background" />
      <div style={styles.container}>
        <div style={styles.headerContainer}>
          <h2 style={styles.headerText}>Genres</h2>
        </div>
        <ul style={styles.scrollContainer}>
          {genres.map((item, index) => (
            <li key={`genre-${index}`} style={styles.listItem}>
              <button onClick={() => handleSelectGenre(item.value)} style={styles.button}>
                <div style={styles.row}>
                  <img src={require('./assets/dot.png')} style={{ width: '2vh', height: '2vh', marginRight: '1vw' }} alt="dot" />
                  <span style={styles.text}>{item.name}</span>
                </div>
              </button>
              <div style={styles.borderContainer}>
                <div style={styles.centeredBorder}></div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const styles = {
  container: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: 'transparent',
    zIndex: 10,
    height: `calc(100vh - 18vh)`,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginLeft: '25px',
  },
  fullScreen: {
    display: 'flex',
    flexDirection: 'column',
    height: '130%',
    alignItems: 'flex-start',
    position: 'relative',
    overflow: 'hidden',
  },
  fullScreenImage: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    zIndex: 0,
  },
  button: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '1vh',
    margin: '1vh 0',
    cursor: 'pointer',
    zIndex: 10,
    textAlign: 'left',
    width: '100%',
  },
  text: {
    color: 'white',
    fontSize: '2.4vh',
    fontWeight: 'bold',
    fontFamily: 'brhendrix',
  },
  scrollContainer: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    position: 'relative', // Changed from 'absolute' to 'relative'
    width: '110%',
    height: `calc(100vh - 18vh)`,
    zIndex: 2,
    overflowY: 'auto',
  },
  borderContainer: {
    width: '100%',
    alignItems: 'center',
    marginTop: '0',
  },
  centeredBorder: {
    marginLeft: '18%',
    width: '110%',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgba(189, 252, 251, 0.3)',
  },
  headerContainer: {
    display: 'flex',
    marginTop: '10px',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginLeft: '35px',
    width: '100%',
    backgroundColor: 'transparent', // Ensure the background is transparent
    zIndex: 10,
  },
  headerText: {
    color: 'white',
    fontSize: '2.5vh',
    fontWeight: 'bold',
    textAlign: 'center', // Center the text horizontally
    fontFamily: 'brhendrix',
  },
  listItem: {

  },
};

export default GenresScreen;
